import { fmtAUD, fmtPct, MONTH_NAMES } from "../lib/format";
import { PushPin, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";

const SRC_LABEL = {
  duffel: "Duffel NDC",
  airline_direct: "Airline Direct",
  serpapi: "Google Flights",
  tripcom: "Trip.com",
  brightdata: "Bright Data",
  zyte: "Zyte",
  kiwi: "Kiwi",
  mock: "Estimated",
  override: "Override",
};

const VERIFIED_SOURCES = new Set([
  "duffel", "serpapi", "tripcom", "airline_direct", "override", "brightdata", "zyte", "kiwi",
]);

export default function FareCell({ cell, isSaa, testid, onVerify, canVerify }) {
  const fare = cell.avg_fare;
  const src = cell.source || "unknown";
  const srcClass = `src-${cell.is_override ? "override" : src}`;
  const isUnverified =
    !cell.is_override && !cell.verified_manually && !VERIFIED_SOURCES.has(src);

  const gapClass = !cell.gap_to_saa_pct
    ? ""
    : cell.gap_to_saa_pct > 15
    ? "text-[color:var(--danger)]"
    : cell.gap_to_saa_pct > 0
    ? "text-[color:var(--warning)]"
    : "text-[color:var(--success)]";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={testid}
            className={`fare-cell ${srcClass} ${isSaa ? "fare-cell-saa" : ""}`}
          >
            <div className="flex items-center justify-between gap-1">
              <span className="font-mono text-sm font-semibold"
                    style={{ color: isUnverified && fare ? "var(--warning)" : "var(--text-primary)",
                              textDecoration: isUnverified && fare ? "line-through dashed" : "none",
                              textDecorationColor: "rgba(245,158,11,0.4)" }}>
                {fmtAUD(fare)}
              </span>
              <div className="flex items-center gap-1">
                {isUnverified && fare && (
                  <span
                    className="font-mono text-[9px] px-1 rounded"
                    style={{
                      background: "rgba(245,158,11,0.18)",
                      color: "var(--warning)",
                      border: "1px solid rgba(245,158,11,0.40)",
                    }}
                    title="Unverified — no live source returned data for this cell"
                  >
                    UNVERIFIED
                  </span>
                )}
                {cell.is_override && (
                  <PushPin size={11} weight="fill" color="#EC4899" />
                )}
                {cell.has_disagreement && (
                  <span
                    className="font-mono text-[9px] px-1 rounded"
                    style={{
                      background: "rgba(168, 85, 247, 0.18)",
                      color: "#C084FC",
                      border: "1px solid rgba(168, 85, 247, 0.40)",
                    }}
                    title={`Source disagreement: ${cell.disagreement_pct > 0 ? "+" : ""}${cell.disagreement_pct}%`}
                  >
                    ⚠ {Math.abs(cell.disagreement_pct)}%
                  </span>
                )}
                {cell.has_fallback && (
                  <span
                    className="font-mono text-[9px] px-1 rounded"
                    style={{
                      background: "rgba(245,158,11,0.15)",
                      color: "var(--warning)",
                    }}
                  >
                    ±{cell.max_offset_days}d
                  </span>
                )}
                {cell.low_confidence && !cell.is_override && fare && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "var(--warning)" }}
                    title="Low confidence"
                  />
                )}
                {canVerify && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onVerify && onVerify(cell); }}
                    title="Verify with airline-direct + SerpAPI"
                    className="opacity-40 hover:opacity-100 transition-opacity"
                    style={{ color: "var(--saa-gold)" }}
                  >
                    <MagnifyingGlass size={11} weight="bold" />
                  </button>
                )}
              </div>
            </div>
            {!isSaa && cell.gap_to_saa_pct != null && (
              <div className={`font-mono text-[10px] mt-0.5 ${gapClass}`}>
                {fmtPct(cell.gap_to_saa_pct)}
              </div>
            )}
            {cell.has_no_flight && !fare && (
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                No flight
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          <div className="text-xs space-y-1">
            <div>
              <span className="font-semibold">{SRC_LABEL[cell.is_override ? "override" : src]}</span>
              {" · "}
              <span style={{ color: "var(--text-secondary)" }}>
                {MONTH_NAMES[cell.month - 1]} {cell.year}
              </span>
            </div>
            {cell.avg_by_source && Object.keys(cell.avg_by_source).length > 1 && (
              <div className="font-mono text-[11px] mt-0.5 space-y-0.5">
                {Object.entries(cell.avg_by_source).map(([s, v]) => (
                  <div key={s}>
                    <span style={{ color: "var(--text-secondary)" }}>{SRC_LABEL[s] || s}:</span>{" "}
                    <span>{fmtAUD(v)}</span>
                  </div>
                ))}
              </div>
            )}
            {cell.has_disagreement && (
              <div className="flex items-center gap-1" style={{ color: "#C084FC" }}>
                <Warning size={10} weight="fill" />
                Source disagreement: {cell.disagreement_pct > 0 ? "SerpAPI " : "Airline "}is{" "}
                {Math.abs(cell.disagreement_pct)}% {cell.disagreement_pct > 0 ? "higher" : "lower"}.
              </div>
            )}
            {cell.samples && cell.samples.length > 0 && !cell.has_disagreement && (
              <div>
                Avg of {cell.sample_count} weekly samples:
                <div className="font-mono text-[11px] mt-0.5">
                  {cell.samples
                    .filter((s) => s.fare != null)
                    .map((s) => fmtAUD(s.fare))
                    .join(" · ")}
                </div>
              </div>
            )}
            {cell.has_fallback && (
              <div className="flex items-center gap-1" style={{ color: "var(--warning)" }}>
                <Warning size={10} /> Includes ±{cell.max_offset_days}d fallback
              </div>
            )}
            {cell.verified_manually && (
              <div style={{ color: "var(--success)" }}>✓ Verified by admin</div>
            )}
            {cell.is_override && cell.override_note && (
              <div style={{ color: "var(--text-secondary)" }}>{cell.override_note}</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
